import React, { useState, useRef } from 'react';
import { MapPinIcon, SendIcon, CheckCircleIcon } from 'lucide-react';
export function Contact() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submittedName, setSubmittedName] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch('https://formspree.io/f/xqedwkgk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        })
      });
      if (res.ok) {
        setSubmittedName(formData.name);
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  return <section id="contact" className="py-20 bg-gray-50" data-id="element-89">
      <div className="container mx-auto px-4" data-id="element-90">
        <div className="text-center mb-16" data-id="element-91">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-92">
            Get in Touch
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-6" data-id="element-93"></div>
          <p className="max-w-2xl mx-auto text-gray-600 text-lg" data-id="element-94">
            Ready to explore what AI can do for your business? Let's start with
            a 30-minute strategy call — no pitch, just clarity.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-95">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8" data-id="element-96">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-97">
              Send us a message
            </h3>

            {status === 'success' ? <div className="flex flex-col items-center justify-center text-center py-12 gap-4" data-id="element-98">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-99">
                  <CheckCircleIcon size={36} className="text-green-600" data-id="element-100" />
                </div>
                <h4 className="text-xl font-bold text-gray-900" data-id="element-101">
                  You're booked in!
                </h4>
                <p className="text-gray-600 max-w-sm" data-id="element-102">
                  Thanks, <strong data-id="element-103">{submittedName || 'there'}</strong> — we
                  received your request and will reach out within 1 business day
                  to confirm your strategy call.
                </p>
                <button onClick={() => setStatus('idle')} className="mt-2 text-blue-600 text-sm font-medium hover:text-blue-700 underline underline-offset-2" data-id="element-104">
                  Submit another message
                </button>
              </div> : <form className="space-y-6" onSubmit={handleSubmit} ref={formRef} data-id="element-105">
                <div className="grid grid-cols-2 gap-6" data-id="element-106">
                  <div data-id="element-107">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-108">
                      Name
                    </label>
                    <input type="text" id="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors" placeholder="Your name" required data-id="element-109" />
                  </div>
                  <div data-id="element-110">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-111">
                      Email
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors" placeholder="work@company.com" required data-id="element-112" />
                  </div>
                </div>
                <div data-id="element-113">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-114">
                    Phone
                  </label>
                  <input type="tel" id="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" placeholder="+1 (555) 000-0000" data-id="element-115" />
                </div>
                <div data-id="element-116">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-117">
                    Message
                  </label>
                  <textarea id="message" rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors" placeholder="Tell us about your project..." required data-id="element-118"></textarea>
                </div>

                {status === 'error' && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-119">
                    Something went wrong. Please try again or email us directly.
                  </div>}

                <button type="submit" disabled={status === 'submitting'} className={`w-full px-6 py-4 ${status === 'submitting' ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5`} data-id="element-120">
                  {status === 'submitting' ? 'Sending...' : 'Book Strategy Call'}
                  <SendIcon size={18} data-id="element-121" />
                </button>
              </form>}
          </div>

          <div className="space-y-8" data-id="element-122">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8" data-id="element-123">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-124">
                Our Location
              </h3>
              <div className="flex items-start gap-4" data-id="element-125">
                <div className="mt-1 bg-gradient-to-br from-blue-50 to-purple-50 p-2 rounded-full border border-purple-100/50" data-id="element-126">
                  <MapPinIcon size={24} className="text-blue-600" data-id="element-127" />
                </div>
                <div data-id="element-128">
                  <h4 className="font-bold text-gray-900 text-lg" data-id="element-129">
                    Headquarters
                  </h4>
                  <p className="text-gray-600 mt-1" data-id="element-130">Pompano Beach, Florida</p>
                  <p className="text-gray-500 text-sm mt-2" data-id="element-131">
                    Serving clients nationwide
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-600 rounded-xl shadow-md p-8 text-white" data-id="element-132">
              <h3 className="text-xl font-bold mb-4" data-id="element-133">Why book a call?</h3>
              <ul className="space-y-4" data-id="element-134">
                <li className="flex items-start gap-3" data-id="element-135">
                  <CheckCircleIcon className="shrink-0 mt-0.5" size={20} data-id="element-136" />
                  <span className="text-blue-50" data-id="element-137">
                    Get a clear assessment of your AI readiness
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-138">
                  <CheckCircleIcon className="shrink-0 mt-0.5" size={20} data-id="element-139" />
                  <span className="text-blue-50" data-id="element-140">
                    Identify 2-3 high-impact automation opportunities
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-141">
                  <CheckCircleIcon className="shrink-0 mt-0.5" size={20} data-id="element-142" />
                  <span className="text-blue-50" data-id="element-143">
                    Understand ROI potential and get a clear pricing path
                    forward
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}